/* oxlint-disable sorare/enforce-monetary-reward-disclaimer -- Non-reward currency (wallet/market/JONUM limits/etc.) — disclaimer does not apply. */
import classNames from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import type { Sport } from '#src/__generated__/globalTypes.ts';
import { Dropdown } from '#src/atoms/dropdowns/Dropdown/index.tsx';
import { StarBall } from '#src/atoms/icons/StarBall/index.tsx';
import { FontAwesomeIcon } from '#src/atoms/icons/index.tsx';
import { iconLogOut } from '#src/atoms/icons/system/iconLogOut.ts';
import { iconSettings } from '#src/atoms/icons/system/iconSettings.ts';
import { iconUser } from '#src/atoms/icons/system/iconUser.ts';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '#src/atoms/layout/flex/index.tsx';
import { BodyL, BodyS, BrandT } from '#src/atoms/typography/index.tsx';
import { Badge } from '#src/atoms/ui/Badge/index.tsx';
import { CloseButton } from '#src/components/dialog/CloseButton/index.tsx';
import { JonumKycReminder } from '#src/components/jonum/JonumKycReminder/index.tsx';
import { LegalWarningBanner } from '#src/components/jonum/LegalWarningBanner/index.tsx';
import { Footer } from '#src/components/navigation/AppNavigation/NavItems/MoreNavItems/Footer/index.tsx';
import { MoreNavItems } from '#src/components/navigation/AppNavigation/NavItems/MoreNavItems/index.tsx';
import { SportSelector } from '#src/components/navigation/AppNavigation/SportSelector/index.tsx';
import { MarketCredits } from '#src/components/navigation/AppNavigation/UserCurrencies/MarketCredits/index.tsx';
import { MoneyBalances } from '#src/components/navigation/AppNavigation/UserCurrencies/MoneyBalances/index.tsx';
import {
  NavItem,
  Separator,
} from '#src/components/navigation/NavItem/index.tsx';
import {
  ANY_SPORT_MY_CLUB,
  ANY_SPORT_SERIES_INVITE,
  MY_SORARE,
  SETTINGS,
} from '#src/constants/__generated__/routes.ts';
import { useConfigContext } from '#src/contexts/config/index.ts';
import { useCurrentUserContext } from '#src/contexts/currentUser/index.ts';
import { useIntlContext } from '#src/contexts/intl/index.ts';
import { useWalletContext } from '#src/contexts/wallet/index.ts';
import { useWalletDrawerContext } from '#src/contexts/walletDrawer/index.tsx';
import { useSeriesNav } from '#src/hooks/commonGame/useSeriesNav.ts';
import { useIsLaptop } from '#src/hooks/device/useIsLaptop.ts';
import { useCurrentUserCurrencies } from '#src/hooks/useCurrentUserCurrencies.tsx';
import { useFeatureFlags } from '#src/hooks/useFeatureFlags.tsx';
import { useInviteLink } from '#src/hooks/useInviteLink.tsx';
import { useReferralProgramConfig } from '#src/hooks/useReferralProgramConfig.ts';
import { glossary, navLabels } from '#src/lib/glossary.ts';
import { generateSportPath } from '#src/lib/routing/generateSportPath.ts';
import { laptopAndAbove } from '#src/style/mediaQuery.ts';
import { regularMaterial } from '#src/style/utils.tsx';

import { MyClubNavItem } from './MyClubNavItem/index.tsx';
import { ProfileButtonContent } from './ProfileButtonContent/index.tsx';
import inviteBg from './assets/invite-bg.webp';

const DropdownContent = styled(Vertical).attrs({ gap: 2 })`
  top: 0;
  padding: var(--double-unit);
  border-radius: var(--double-unit);
  ${regularMaterial}

  @media ${laptopAndAbove} {
    width: 375px;
  }
`;

const StyledSBHorizontal = styled(SBHorizontal)`
  width: 100%;
`;

const Box = styled(Link)`
  display: flex;
  gap: var(--double-unit);
  align-items: flex-start;
  flex-direction: column;
  padding: var(--double-unit);
  border: 1px solid var(--c-nd-150);
  border-radius: var(--double-unit);
  flex-grow: 1;
  align-self: stretch;

  &:hover {
    background-color: rgba(var(--c-rgb-white), 0.04);
  }
`;

const InviteBanner = styled(Link)`
  display: flex;
  gap: var(--unit);
  flex-direction: column;
  align-items: flex-start;
  padding: var(--double-unit);
  border: 1px solid var(--c-set-static-surface-primary);
  border-radius: var(--double-unit);
  flex-grow: 1;
  align-self: stretch;
  background:
    linear-gradient(transparent, transparent),
    url(${inviteBg}) top / cover no-repeat;
  box-shadow: 0 0 20px rgba(var(--c-rgb-set-surface-primary), 0.25);
  overflow: hidden;
  position: relative;

  &:hover {
    background:
      linear-gradient(var(--c-nd-100), var(--c-nd-100)),
      url(${inviteBg}) top / cover no-repeat;
  }
`;

const MySorareAndInviteFriends = styled(Horizontal)`
  display: flex;
  gap: var(--double-unit);
  &.setInviteFriends {
    flex-direction: column;
  }
`;

type Props = { sport: Sport; size?: 'small' | 'medium' };
export const UserNav = ({ sport, size }: Props) => {
  const { currentUser } = useCurrentUserContext();
  const inviteLink = useInviteLink();
  const { showDrawer } = useWalletDrawerContext();
  const { formatMessage } = useIntl();
  const isLaptopAndAbove = useIsLaptop();
  const walletContext = useWalletContext();
  const { fiatCurrency } = useCurrentUserCurrencies();
  const { userFiatAmount } = useReferralProgramConfig();
  const { formatNumber } = useIntlContext();
  const { cardSet } = useConfigContext();
  const { maximumInviteTasksAsReferrer } = cardSet || {};
  const {
    flags: { enableInviteFriends = false, jonumUserInfoCollection = false },
  } = useFeatureFlags();
  const seriesNav = useSeriesNav();
  const amount =
    userFiatAmount &&
    formatNumber(userFiatAmount, {
      style: 'currency',
      currency: fiatCurrency,
      maximumFractionDigits: 0,
    });

  return (
    <Dropdown
      transparent
      forceDesktopBehavior={isLaptopAndAbove}
      label={({ onClick, open }) => (
        <button type="button" onClick={onClick}>
          <ProfileButtonContent sport={sport} active={open} size={size} />
        </button>
      )}
      side={isLaptopAndAbove ? 'bottom' : 'left'}
      gap={16}
    >
      {({ closeDropdown }) => (
        <DropdownContent>
          {!isLaptopAndAbove && (
            <SBHorizontal>
              <SportSelector displaySelectedSportName />
              <CloseButton onClose={closeDropdown} />
            </SBHorizontal>
          )}
          <MyClubNavItem
            to={generateSportPath(ANY_SPORT_MY_CLUB, { sport })}
            onClick={closeDropdown}
          />
          <MySorareAndInviteFriends
            className={classNames({
              setInviteFriends: seriesNav && enableInviteFriends,
            })}
          >
            <Box to={MY_SORARE}>
              <StarBall width={16} height={16} />
              <Vertical gap={0.5}>
                <BodyL bold>{formatMessage(navLabels.mySorare)}</BodyL>
                <BodyS color="var(--c-nd-600)">
                  <FormattedMessage defaultMessage="See what’s new" />
                </BodyS>
              </Vertical>
            </Box>
            {seriesNav && enableInviteFriends ? (
              !!maximumInviteTasksAsReferrer && (
                <InviteBanner
                  to={generateSportPath(ANY_SPORT_SERIES_INVITE, { sport })}
                  onClick={closeDropdown}
                >
                  <BrandT>
                    <FormattedMessage defaultMessage="Invite friends" />
                  </BrandT>
                  <BodyS color="var(--c-nd-700)">
                    <FormattedMessage
                      defaultMessage="Earn gems, cards, and Essence for every new friend referred to the Set (max {max}/Set)."
                      values={{
                        max: maximumInviteTasksAsReferrer,
                      }}
                    />
                  </BodyS>
                </InviteBanner>
              )
            ) : (
              <Box to={inviteLink}>
                <StyledSBHorizontal>
                  <FontAwesomeIcon icon={iconUser} />
                  <Badge
                    badgeContent={currentUser?.unclaimedReferralRewardsCount}
                    right={1}
                  />
                </StyledSBHorizontal>
                <Vertical gap={0.5}>
                  <BodyL bold>{formatMessage(navLabels.inviteFriends)}</BodyL>
                  <BodyS color="var(--c-nd-600)">
                    <FormattedMessage
                      defaultMessage="Earn up to {amount}"
                      values={{ amount }}
                    />
                  </BodyS>
                </Vertical>
              </Box>
            )}
          </MySorareAndInviteFriends>
          {!!jonumUserInfoCollection && <JonumKycReminder />}
          {!isLaptopAndAbove && (
            <>
              <MoneyBalances onClick={showDrawer} />
              <button onClick={showDrawer} type="button">
                <MarketCredits sport={sport} />
              </button>
            </>
          )}
          <Vertical>
            <NavItem
              icon={() => <FontAwesomeIcon icon={iconSettings} />}
              label={formatMessage(navLabels.settings)}
              to={SETTINGS}
            />
            {!isLaptopAndAbove && <MoreNavItems noFooter />}

            <Separator />
            <NavItem
              icon={() => <FontAwesomeIcon icon={iconLogOut} />}
              label={formatMessage(glossary.logOut)}
              onClick={() => {
                walletContext.logOut();
                closeDropdown();
              }}
            />
            {!isLaptopAndAbove && (
              <>
                <Separator />
                <Footer />
              </>
            )}
          </Vertical>
          <LegalWarningBanner mobileOnly />
        </DropdownContent>
      )}
    </Dropdown>
  );
};
